import { PlayCircleIcon } from "lucide-react";

import { PhotoWithBlurhash } from "@/components/media/photo-with-blurhash";
import { cn } from "@/lib/utils";

import { EmptyHint } from "./detail";

type Photo = { url?: string | null; blurhash?: string | null };
type Teaser = {
  url?: string | null;
  previewUrl?: string | null;
  thumbnailUrl?: string | null;
  thumbnailBlurhash?: string | null;
};

/** Photo grid + teaser/trailer thumbnails for a resource's media bundle. */
export function MediaGallery({
  media,
  aspect = "portrait",
}: {
  media?: { photos?: Photo[] | null; teasers?: Teaser[] | null } | null;
  aspect?: "portrait" | "landscape";
}) {
  const photos = (media?.photos ?? []).filter((p) => p?.url);
  const teasers = (media?.teasers ?? []).filter((t) => t?.url || t?.previewUrl);

  if (!photos.length && !teasers.length)
    return <EmptyHint>No media.</EmptyHint>;

  return (
    <div className="space-y-5">
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
          {photos.map((p, i) => (
            <PhotoWithBlurhash
              key={i}
              url={p.url!}
              blurhash={p.blurhash ?? ""}
              className={cn(
                "w-full rounded-md object-cover ring-1 ring-border",
                aspect === "portrait" ? "aspect-[2/3]" : "aspect-video",
              )}
            />
          ))}
        </div>
      )}

      {teasers.length > 0 && (
        <div className="space-y-2">
          {photos.length > 0 && (
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Teasers
            </p>
          )}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {teasers.map((t, i) => {
              const href = t.url || t.previewUrl || undefined;
              const thumb = t.thumbnailUrl || t.previewUrl;
              return (
                <a
                  key={i}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  className="group relative block aspect-video overflow-hidden rounded-md bg-muted ring-1 ring-border"
                >
                  {thumb ? (
                    <PhotoWithBlurhash
                      url={thumb}
                      blurhash={t.thumbnailBlurhash ?? ""}
                      className="h-full w-full object-cover transition-transform group-hover:scale-105"
                    />
                  ) : null}
                  <span className="absolute inset-0 flex items-center justify-center bg-black/20 text-white opacity-90 transition-opacity group-hover:opacity-100">
                    <PlayCircleIcon className="size-8 drop-shadow" />
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
